import { useEffect, useId, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, type InstallResult } from '../lib/api'

type CtaState = 'idle' | 'confirm' | 'running' | 'done' | 'failed'

/** Tail kept on screen even though the server already caps the captured log at 8000 chars — a
 *  failed installer's useful line (the actual error) is almost always at the END. */
const LOG_TAIL_CHARS = 2000

/**
 * The Claude-only assisted-UPDATE affordance, always offered on AiAgentSection's signed-in Claude
 * card (with its floor warning when the CLI is below Niobe's version floor, `versionOk === false`)
 * and on the chat's "too old for this model" card (ChatUpdateRequiredCard, AB#370).
 *
 * Same state machine and same job plumbing as {@link ClaudeInstallCta} — `POST /agent/update` runs
 * the very same native installer, which installs the latest build over the existing one — with two
 * differences that matter:
 *
 * 1. **It is additive, never a replacement.** An outdated CLI is a *usable* CLI: the chat keeps
 *    working and the card keeps every setting it already had. This renders above
 *    LocalSkillsSettings, not instead of it.
 * 2. **"Done" is not automatically "current".** `InstallResult.versionOk` tells "updated" apart
 *    from "updated and still too old" — a corporate mirror pinned to an old build, or an installer
 *    that exited cleanly having done nothing. Reporting the second as success is how a member ends
 *    up chasing a warning that never goes away.
 */
export function ClaudeUpdateCta({
  currentVersion,
  minVersion,
  onUpdated,
  note,
}: {
  currentVersion: string | undefined
  minVersion: string
  /** Called with the installer's verdict, so a caller can tell "current" from "still too old". */
  onUpdated: (result: InstallResult) => void
  /** Replaces the idle sentence — the chat's update card (AB#370) says why it is blocking. */
  note?: string
}) {
  const { t } = useLang()
  const [state, setState] = useState<CtaState>('idle')
  const [jobId, setJobId] = useState<string | null>(null)
  /** Only set when triggerUpdate() itself throws (no job was ever created to poll for a result). */
  const [triggerError, setTriggerError] = useState<string | null>(null)
  const logId = useId()

  const jobQuery = useQuery({
    queryKey: ['agent-job', jobId],
    queryFn: () => api.agentJob(jobId as string),
    enabled: state === 'running' && !!jobId,
    refetchInterval: 2000,
  })
  const job = jobQuery.data
  const result = job?.result as InstallResult | undefined

  useEffect(() => {
    if (state !== 'running' || !job) return
    if (job.status === 'cancelled') {
      setState('idle')
      setJobId(null)
    } else if (job.status === 'done') {
      // 'done' only means installAgent() resolved without throwing — `ok` is the actual verdict,
      // same care as ClaudeInstallCta.
      if (result?.ok) {
        setState('done')
        onUpdated(result)
      } else {
        setState('failed')
      }
    } else if (job.status === 'error') {
      setState('failed')
    }
  }, [state, job, result, onUpdated])

  async function confirmUpdate(): Promise<void> {
    setTriggerError(null)
    try {
      const { jobId: id } = await api.triggerUpdate('claude')
      setJobId(id)
      setState('running')
    } catch (e) {
      setTriggerError(e instanceof Error ? e.message : String(e))
      setState('failed')
    }
  }

  async function cancelUpdate(): Promise<void> {
    if (jobId) await api.cancelJob(jobId)
  }

  if (state === 'idle') {
    return (
      <div className="claude-update">
        <p className="claude-update-note">
          {note ??
            t('claude_update_note')
              .replace('{n}', () => currentVersion ?? '?')
              .replace('{min}', () => minVersion)}
        </p>
        <button className="mini ok" onClick={() => setState('confirm')}>
          {t('claude_update_cta')}
        </button>
      </div>
    )
  }

  if (state === 'confirm') {
    return (
      <div className="claude-update" role="status" aria-live="polite">
        <p className="claude-update-title">
          <b>{t('claude_update_confirm_title')}</b>
        </p>
        <p>{t('claude_update_confirm_body')}</p>
        <div className="claude-update-actions">
          <button className="mini ok" onClick={() => void confirmUpdate()}>
            {t('claude_update_confirm_yes')}
          </button>
          <button className="mini ghost" onClick={() => setState('idle')}>
            {t('claude_install_confirm_cancel')}
          </button>
        </div>
      </div>
    )
  }

  if (state === 'running') {
    // The installer's own script echoes step-by-step lines through the job's progress channel.
    const latestLine = job?.progress?.at(-1)?.target
    return (
      <div className="claude-update" role="status" aria-live="polite">
        <span className="thinking">
          <span className="dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          {t('claude_update_running')}
        </span>
        {latestLine && <p className="claude-install-progress mono">{latestLine}</p>}
        <div className="claude-update-actions">
          <button className="mini ghost" onClick={() => void cancelUpdate()}>
            {t('stop_generating')}
          </button>
        </div>
      </div>
    )
  }

  if (state === 'done') {
    const version = result?.version ?? '?'
    // versionOk === undefined (the new version could not be read) reports plain success rather than
    // claiming either way — the card's own badge is driven by the fresh /agent/status anyway.
    const stillOld = result?.versionOk === false
    return (
      <div className="claude-update" role="status" aria-live="polite">
        <p className={stillOld ? 'claude-update-note' : 'claude-install-success'}>
          {stillOld
            ? t('claude_update_done_still_old').replace('{n}', () => version).replace('{min}', () => minVersion)
            : t('claude_update_done_version').replace('{n}', () => version)}
        </p>
      </div>
    )
  }

  // state === 'failed' — the CLI that is already installed keeps working, so this is informational.
  const log = (result?.log ?? triggerError ?? '').slice(-LOG_TAIL_CHARS)
  return (
    <div className="claude-update" role="status" aria-live="polite">
      <p className="claude-install-failed-title">
        <b>{t('claude_update_failed')}</b>
      </p>
      {log !== '' && (
        <>
          <p id={logId} className="claude-install-log-label">
            {t('claude_install_log_label')}
          </p>
          <pre className="mono claude-install-log" tabIndex={0} aria-labelledby={logId}>
            {log}
          </pre>
        </>
      )}
      <button className="mini ghost" onClick={() => setState('idle')}>
        {t('claude_login_retry')}
      </button>
    </div>
  )
}
